import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { landingRoute } from '../config/modules';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import FormField from '../components/ui/FormField';
import PasswordInput from '../components/ui/PasswordInput';
import Modal, { ModalCancelButton } from '../components/ui/Modal';
import styles from '../styles/LoginPage.module.css';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  // "Forgot password" used to be a modal that told you to go and ask somebody.
  // It now actually asks them for you: there is no mail provider in this app,
  // so the honest version of this button notifies every admin in-app and on
  // their phone, with a link to the screen where they issue the reset.
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState('');
  const [forgotError, setForgotError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      // Where each role lands is decided in one place — see landingRoute().
      navigate(landingRoute(result.user));
    } else {
      setError(result.error || 'Login failed. Please try again.');
    }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    setForgotError('');
    try {
      const response = await fetch('/api/auth/password-reset/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail || email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Could not send that request');
      setForgotSent(data.message);
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotBusy(false);
    }
  };

  const closeForgot = () => {
    setShowForgot(false);
    setForgotSent('');
    setForgotError('');
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginBox}>
        <div className={styles.loginHeader}>
          <Logo />
          <p>WTSN Hub - OnlyFans Management App</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.loginForm}>
          <FormField label="Email">
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </FormField>

          <FormField label="Password">
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </FormField>

          <button type="button" className={styles.forgotLink} onClick={() => setShowForgot(true)}>
            Forgot password?
          </button>

          {error && <Alert variant="danger">{error}</Alert>}

          <Button type="submit" variant="primary" loading={loading} className={styles.loginButton}>
            Sign In
          </Button>
        </form>

        <div className={styles.loginFooter}>
          <p>
            Have an invite code?{' '}
            <Link to="/register" className={styles.signupLink}>Sign up</Link>
          </p>
        </div>
      </div>

      <Modal
        open={showForgot}
        onClose={closeForgot}
        title="Forgot your password?"
        footer={
          forgotSent
            ? <Button onClick={closeForgot}>Done</Button>
            : (
              <>
                <ModalCancelButton onClick={closeForgot}>Cancel</ModalCancelButton>
                <Button onClick={requestReset} loading={forgotBusy} disabled={!(forgotEmail || email).includes('@')}>
                  Ask for a reset
                </Button>
              </>
            )
        }
      >
        {forgotSent ? (
          <p>{forgotSent}</p>
        ) : (
          <form onSubmit={requestReset} className={styles.forgotForm}>
            <p className={styles.forgotBlurb}>
              Passwords are stored scrambled, so nobody — not even an admin — can read yours back.
              Tell us your address and your admin will send you a one-time link to set a new one.
            </p>
            <FormField label="Email you sign in with">
              <input
                type="email"
                value={forgotEmail || email}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="you@agency.com"
                autoComplete="username"
              />
            </FormField>
            {forgotError && <Alert variant="danger">{forgotError}</Alert>}
          </form>
        )}
      </Modal>
    </div>
  );
}

export default LoginPage;
